// src/api.js
import { devLog, devWarn } from "./utils/devLog";
import {
  parseProjectSlugFromDetailUrl,
  projectDetailSlugMatchesRequest,
} from "./utils/projectSlug";

const DEFAULT_API = "https://portal.proppins.com/api/v1";
const DEFAULT_IMAGE = "https://portal.proppins.com/storage/";

export const BASE_URL =
  (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "") || DEFAULT_API;
export const IMAGE_URL =
  (process.env.REACT_APP_IMAGE_BASE_URL || "").trim().replace(/\/$/, "") || DEFAULT_IMAGE;

export const BRANDING_API_URL = `${BASE_URL}/public/branding`;

/**
 * Turn API paths like `storage/app-settings/site_logo_….png` into absolute storage URLs.
 * `IMAGE_URL` is typically `https://portal.proppins.com/storage/`.
 */
export function resolvePublicAssetUrl(relativePath) {
  if (!relativePath || typeof relativePath !== "string") return null;
  const trimmed = relativePath.trim();
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  const normalized = trimmed.replace(/^\/+/, "");
  const withoutStoragePrefix = normalized.replace(/^storage\//, "");
  const base = String(IMAGE_URL).replace(/\/$/, "");
  return `${base}/${withoutStoragePrefix}`;
}

/** City/area watermark on explore-project locality cards (`city_icon_url` from home/search APIs). */
export function resolveExploreCityIconUrl(item, sectionFallback = "") {
  const raw =
    item?.city_icon_url ??
    item?.cityIconUrl ??
    item?.icon_url ??
    item?.icon ??
    sectionFallback ??
    "";
  return resolvePublicAssetUrl(typeof raw === "string" ? raw : String(raw ?? ""));
}

/** Fetch timeout — AbortSignal.timeout is missing in some browsers (older Safari); avoid silent fetch failure. */
export function createTimeoutSignal(ms) {
  if (typeof AbortSignal !== "undefined" && typeof AbortSignal.timeout === "function") {
    return AbortSignal.timeout(ms);
  }
  const controller = new AbortController();
  setTimeout(() => controller.abort(), ms);
  return controller.signal;
}


// ---- Internal utility to parse JSON safely ----
async function parseJsonOrThrow(res) {
  const raw = await res.text();
  let data;
  try {
    data = raw ? JSON.parse(raw) : {};
  } catch {
    throw new Error(
      `Non-JSON response (${res.status} ${res.statusText}): ${raw.slice(0, 180)}`
    );
  }
  return data;
}

const inflightRequests = new Map(); // url -> Promise
const memoryCache = new Map(); // url -> { data, expiresAt }

const SS_PREFIX = "api_cache_v1:";

/**
 * GET caching (memory + sessionStorage). Enabled by default in production.
 * Set REACT_APP_ENABLE_API_CACHE=false to disable (e.g. local debugging).
 */
const API_GET_CACHE_ENABLED = process.env.REACT_APP_ENABLE_API_CACHE !== "false";

if (!API_GET_CACHE_ENABLED && typeof sessionStorage !== "undefined") {
  try {
    const toRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(SS_PREFIX)) toRemove.push(k);
    }
    toRemove.forEach((k) => sessionStorage.removeItem(k));
  } catch {
    /* ignore */
  }
  memoryCache.clear();
}

// ✅ Endpoint-based TTL (fast refresh for search-screen) — only used when API_GET_CACHE_ENABLED
const getCacheTTL = (urlStr) => {
  if (urlStr.includes("search/suggestions")) return 30000;
  if (
    urlStr.includes("home-screen") ||
    urlStr.includes("villa-buy-home-screen") ||
    urlStr.includes("plot-buy-home-screen")
  ) {
    return 300000; // 5 min — home payloads are expensive and city-scoped
  }
  if (urlStr.includes("property-categories")) return 600000; // 10 min
  if (urlStr.includes("search-screen")) return 300000; // 5 mins for search listings
  if (urlStr.includes("nearby-projects")) return 300000; // ✅ 5 mins for map nearby search
  if (urlStr.includes("property-news/")) return 60000; // 1 min for property news articles
  // Project detail endpoints rarely change and are expensive — cache 10 min
  if (
    /\/(projects|villa-projects|plot-projects|apartments|villas|plots)\//.test(urlStr) ||
    /\/(projects|villa-projects|plot-projects)\/[^/?#]+(\?|#|$)/.test(urlStr)
  ) {
    return 600000;
  }
  return 10000; // Default 10s
};

/** Project detail payloads can exceed the default 15s client timeout on slow backend paths. */
function resolveFetchTimeoutMs(urlStr, options = {}) {
  if (Number.isFinite(options.timeoutMs) && options.timeoutMs > 0) {
    return options.timeoutMs;
  }
  if (
    /\/(projects|villa-projects|plot-projects)\/[^/?#]+(\?|#|$)/.test(urlStr)
  ) {
    return 45000;
  }
  return 15000;
}

function isFetchTimeoutError(err) {
  const msg = String(err?.message || err || "");
  return (
    err?.name === "AbortError" ||
    /signal timed out|aborted|timeout/i.test(msg)
  );
}

const getAuthToken = () => {
  try {
    return localStorage.getItem("auth_token");
  } catch {
    return null;
  }
};

const withAuthHeaders = (headers = {}) => {
  const token = getAuthToken();
  if (token) return { ...headers, Authorization: `Bearer ${token}` };
  return headers;
};

// ✅ SessionStorage cache helpers (survives refresh)
function ssGet(urlStr) {
  try {
    const raw = sessionStorage.getItem(SS_PREFIX + urlStr);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.data || !parsed?.expiresAt) return null;
    if (parsed.expiresAt < Date.now()) return null;
    return parsed.data;
  } catch {
    return null;
  }
}

function ssSet(urlStr, data, ttl) {
  try {
    sessionStorage.setItem(
      SS_PREFIX + urlStr,
      JSON.stringify({ data, expiresAt: Date.now() + ttl })
    );
  } catch {
    // ignore quota / private mode errors
  }
}

function isProjectDetailUrl(urlStr) {
  try {
    const url = new URL(urlStr);
    return /\/projects\/[^/]+$/i.test(url.pathname);
  } catch {
    return false;
  }
}

function isValidProjectDetailCacheEntry(cacheKey, data) {
  if (!isProjectDetailUrl(cacheKey)) return true;
  const routeSlug = parseProjectSlugFromDetailUrl(cacheKey);
  if (!routeSlug) return true;
  return projectDetailSlugMatchesRequest(data, routeSlug);
}

function purgeCacheEntry(cacheKey) {
  memoryCache.delete(cacheKey);
  try {
    sessionStorage.removeItem(SS_PREFIX + cacheKey);
  } catch {
    /* ignore */
  }
}

/** Bypass broken upstream HTTP caches that ignore the project slug in the path. */
function buildNetworkFetchUrl(cacheKey, attempt = 0) {
  if (!isProjectDetailUrl(cacheKey)) return cacheKey;
  const fetchUrl = new URL(cacheKey);
  fetchUrl.searchParams.set("_cb", `${Date.now()}_${attempt}_${Math.random().toString(36).slice(2, 8)}`);
  return fetchUrl.toString();
}

/** Drop poisoned project-detail entries from memory + sessionStorage. */
export function purgeInvalidProjectDetailCaches() {
  for (const [key, entry] of memoryCache.entries()) {
    if (!isProjectDetailUrl(key)) continue;
    if (!isValidProjectDetailCacheEntry(key, entry?.data)) purgeCacheEntry(key);
  }
  try {
    const toRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const storageKey = sessionStorage.key(i);
      if (!storageKey || !storageKey.startsWith(SS_PREFIX)) continue;
      const cacheKey = storageKey.slice(SS_PREFIX.length);
      if (!isProjectDetailUrl(cacheKey)) continue;
      const raw = sessionStorage.getItem(storageKey);
      if (!raw) continue;
      try {
        const parsed = JSON.parse(raw);
        if (!isValidProjectDetailCacheEntry(cacheKey, parsed?.data)) toRemove.push(storageKey);
      } catch {
        toRemove.push(storageKey);
      }
    }
    toRemove.forEach((k) => sessionStorage.removeItem(k));
  } catch {
    /* ignore */
  }
}

async function execFetch(networkUrlStr, ttl, fetchOptions = {}, cacheKey = networkUrlStr) {
  const timeoutMs = resolveFetchTimeoutMs(networkUrlStr, fetchOptions);
  const maxAttempts = isProjectDetailUrl(cacheKey) ? 4 : 2;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const requestUrl =
      attempt === 0
        ? networkUrlStr
        : buildNetworkFetchUrl(cacheKey, attempt);

    try {
      const res = await fetch(requestUrl, {
        headers: withAuthHeaders({ Accept: "application/json" }),
        signal: createTimeoutSignal(timeoutMs),
      });

      const status = res.status;
      const data = await parseJsonOrThrow(res);

      const msg = (data?.message || "").toString();
      const isRateLimited = status === 429 || /too\s*many\s*attempts/i.test(msg);
      if (isRateLimited && attempt < maxAttempts - 1) {
        await new Promise((r) => setTimeout(r, 700));
        continue;
      }

      if (
        isProjectDetailUrl(cacheKey) &&
        !isValidProjectDetailCacheEntry(cacheKey, data) &&
        attempt < maxAttempts - 1
      ) {
        purgeCacheEntry(cacheKey);
        await new Promise((r) => setTimeout(r, 120));
        continue;
      }

      // Cache only when TTL > 0 (ttl 0 = skipCache / personal endpoints — avoid stale “static” lists)
      if (ttl > 0 && isValidProjectDetailCacheEntry(cacheKey, data)) {
        memoryCache.set(cacheKey, { data, expiresAt: Date.now() + ttl });
        ssSet(cacheKey, data, ttl);
      }

      return data;
    } catch (err) {
      if (isFetchTimeoutError(err) && attempt < maxAttempts - 1) {
        await new Promise((r) => setTimeout(r, 400));
        continue;
      }
      throw err;
    }
  }
  throw new Error("Request failed after retries");
}

function backgroundRefresh(cacheKey, ttl, fetchOptions = {}) {
  if (inflightRequests.has(cacheKey)) return;
  const networkUrl = buildNetworkFetchUrl(cacheKey);
  const p = execFetch(networkUrl, ttl, fetchOptions, cacheKey).finally(() =>
    inflightRequests.delete(cacheKey),
  );
  inflightRequests.set(cacheKey, p);
}

/**
 * ✅ getJson(path, params, options)
 * options:
 * - staleWhileRevalidate: boolean
 * - skipCache: boolean — bypass read/write cache (use for user-specific GETs that must always be fresh)
 * - timeoutMs: number — override fetch abort timeout (project detail defaults to 45s)
 */
export async function getJson(path, params, options = {}) {
  const url = new URL(`${BASE_URL}/${String(path).replace(/^\/+/, "")}`);
  if (params && typeof params === "object") {
    for (const [k, v] of Object.entries(params)) url.searchParams.set(k, v);
  }
  const cacheKey = url.toString();
  const ttl = API_GET_CACHE_ENABLED ? getCacheTTL(cacheKey) : 0;
  const now = Date.now();

  const skipCache = options.skipCache || !API_GET_CACHE_ENABLED;

  if (skipCache) {
    purgeCacheEntry(cacheKey);
    if (inflightRequests.has(cacheKey)) return inflightRequests.get(cacheKey);
    const networkUrl = buildNetworkFetchUrl(cacheKey);
    const p = execFetch(networkUrl, 0, options, cacheKey).finally(() =>
      inflightRequests.delete(cacheKey),
    );
    inflightRequests.set(cacheKey, p);
    return p;
  }

  // ✅ 1) memory cache
  const mem = memoryCache.get(cacheKey);
  if (mem && mem.expiresAt > now) {
    if (isValidProjectDetailCacheEntry(cacheKey, mem.data)) {
      if (options.staleWhileRevalidate) backgroundRefresh(cacheKey, ttl, options);
      return mem.data;
    }
    purgeCacheEntry(cacheKey);
  }

  // ✅ 2) session cache (survives refresh)
  const ss = ssGet(cacheKey);
  if (ss) {
    if (isValidProjectDetailCacheEntry(cacheKey, ss)) {
      memoryCache.set(cacheKey, { data: ss, expiresAt: now + ttl });
      if (options.staleWhileRevalidate) backgroundRefresh(cacheKey, ttl, options);
      return ss;
    }
    purgeCacheEntry(cacheKey);
  }

  // ✅ 3) inflight coalescing
  if (inflightRequests.has(cacheKey)) return inflightRequests.get(cacheKey);

  // ✅ 4) fetch
  const networkUrl = buildNetworkFetchUrl(cacheKey);
  const p = execFetch(networkUrl, ttl, options, cacheKey).finally(() =>
    inflightRequests.delete(cacheKey),
  );
  inflightRequests.set(cacheKey, p);
  return p;
}

// Backwards compatibility
export const fetchData = getJson;

export async function postJson(path, body) {
  const url = `${BASE_URL}/${String(path).replace(/^\/+/, "")}`;

  const res = await fetch(url, {
    method: "POST",
    headers: withAuthHeaders({
      "Content-Type": "application/json",
      Accept: "application/json",
    }),
    body: JSON.stringify(body ?? {}),
    signal: createTimeoutSignal(15000),
  });

  const data = await parseJsonOrThrow(res);

  if (!res.ok) {
    const err = new Error(
      data?.message ||
      (data?.errors && Object.values(data.errors).flat()[0]) ||
      `Request failed with status ${res.status}`
    );
    err.response = { status: res.status, data };
    if (res.status === 429) {
      const retryAfter = res.headers.get("Retry-After");
      if (retryAfter) {
        const n = parseInt(retryAfter, 10);
        if (Number.isFinite(n) && n > 0) err.retryAfterSeconds = n;
      }
    }
    throw err;
  }
  return data;
}

export async function putJson(path, body) {
  const url = `${BASE_URL}/${String(path).replace(/^\/+/, "")}`;

  const res = await fetch(url, {
    method: "PUT",
    headers: withAuthHeaders({
      "Content-Type": "application/json",
      Accept: "application/json",
    }),
    body: JSON.stringify(body ?? {}),
    signal: createTimeoutSignal(15000),
  });

  const data = await parseJsonOrThrow(res);

  if (!res.ok) {
    const err = new Error(
      data?.message ||
      (data?.errors && Object.values(data.errors).flat()[0]) ||
      `Request failed with status ${res.status}`
    );
    err.response = { status: res.status, data };
    throw err;
  }
  return data;
}

export async function deleteJson(path, body) {
  const res = await fetch(`${BASE_URL}/${String(path).replace(/^\/+/, "")}`, {
    method: "DELETE",
    headers: withAuthHeaders({
      "Content-Type": "application/json",
      Accept: "application/json",
    }),
    body: JSON.stringify(body ?? {}),
    signal: createTimeoutSignal(15000),
  });

  const data = await parseJsonOrThrow(res);

  if (!res.ok) {
    const err = new Error(
      data?.message ||
      (data?.errors && Object.values(data.errors).flat()[0]) ||
      `Request failed with status ${res.status}`
    );
    err.response = { status: res.status, data };
    throw err;
  }
  return data;
}

/**
 * Upload file(s) with form data
 * @param {string} path - API endpoint path
 * @param {FormData} formData - FormData object with files and fields
 * @returns {Promise}
 */
export async function postFormData(path, formData) {
  const url = `${BASE_URL}/${String(path).replace(/^\/+/, "")}`;
  const token = getAuthToken();
  
  const headers = { Accept: "application/json" };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const res = await fetch(url, {
    method: "POST",
    headers,
    body: formData,
    signal: createTimeoutSignal(60000), // Longer timeout for file uploads
  });

  const data = await parseJsonOrThrow(res);

  if (!res.ok) {
    const err = new Error(
      data?.message ||
      (data?.errors && Object.values(data.errors).flat()[0]) ||
      `Request failed with status ${res.status}`
    );
    err.response = { status: res.status, data };
    throw err;
  }
  return data;
}

// Wishlist API functions
/**
 * Get wishlist for a specific property type
 * @param {string} propertyType - 'apartment', 'villa', or 'plot' (optional, if not provided gets all)
 * @returns {Promise}
 */
export async function getWishlist(propertyType = null) {
  // If no propertyType specified, aggregate from all category-specific endpoints
  // This ensures proper categorization and avoids backend issues
  if (!propertyType) {
    devLog("Loading wishlist for all categories (apartment, villa, plot)");

    // Load all categories in parallel
    const results = {
      success: true,
      data: []
    };

    try {
      const [aptRes, villaRes, plotRes] = await Promise.all([
        getJson("wishlist/apartment").catch(e => {
          devWarn("Failed to load apartment wishlist:", e.message);
          return { data: [] };
        }),
        getJson("wishlist/villa").catch(e => {
          devWarn("Failed to load villa wishlist:", e.message);
          return { data: [] };
        }),
        getJson("wishlist/plot").catch(e => {
          devWarn("Failed to load plot wishlist:", e.message);
          return { data: [] };
        })
      ]);

      // Combine all results
      results.data = [
        ...(aptRes?.data || []),
        ...(villaRes?.data || []),
        ...(plotRes?.data || [])
      ];

      devLog("Aggregated wishlist:", {
        apartments: (aptRes?.data || []).length,
        villas: (villaRes?.data || []).length,
        plots: (plotRes?.data || []).length,
        total: results.data.length
      });

      return results;
    } catch (error) {
      console.error("Failed to load all wishlist categories:", error.message);
      return results;
    }
  }

  // Normalize propertyType to match API expectations
  const normalized = String(propertyType).toLowerCase().trim();
  const categoryMap = {
    apartment: "apartment",
    apartments: "apartment",
    apt: "apartment",
    apts: "apartment",
    villa: "villa",
    villas: "villa",
    plot: "plot",
    plots: "plot",
    land: "plot",
  };

  const category = categoryMap[normalized] || normalized;
  devLog("Getting wishlist for category:", category);
  return getJson(`wishlist/${category}`);
}

export async function addToWishlistApi(item) {
  // Extract property_type and project_id from the item
  const propertyType = item?.property_type || "apartment";
  const projectId = item?.project_id || item?.id;

  if (!projectId) {
    throw new Error("Project ID is required for adding to wishlist");
  }

  devLog("📤 Adding to wishlist:", `wishlist/${propertyType}/${projectId}`);
  devLog("📦 Payload:", item);

  // POST to category-specific endpoint with project_id in URL path
  return postJson(`wishlist/${propertyType}/${projectId}`, {});
}

export async function removeFromWishlistApi(projectId, propertyType) {
  if (!projectId) throw new Error("Project ID is required for deletion");

  // Normalize property type
  const normalized = String(propertyType).toLowerCase().trim();
  const categoryMap = {
    apartment: "apartment",
    apartments: "apartment",
    apt: "apartment",
    apts: "apartment",
    villa: "villa",
    villas: "villa",
    plot: "plot",
    plots: "plot",
    land: "plot",
  };

  const category = categoryMap[normalized] || "apartment";
  devLog("🗑️  Deleting from wishlist:", `wishlist/${category}/${projectId}`);

  // DELETE from category-specific endpoint with project_id in URL path
  return deleteJson(`wishlist/${category}/${projectId}`, {});
}

/**
 * Submit a review for a project (pending approval).
 * @param {number|string} projectId - Project ID
 * @param {Object} body - { rating: number (1-5), review_text: string }
 * @returns {Promise} { success, message, data: { review_id, status, estimated_approval_time } }
 */
export async function submitProjectReview(projectId, body) {
  if (!projectId) throw new Error("Project ID is required to submit a review");
  return postJson(`projects/${projectId}/reviews`, body);
}

/**
 * FAQ helpful / not-helpful vote (public; backend may rate-limit by IP).
 * @param {number|string} faqId
 * @param {boolean} helpful
 * @returns {Promise<{ success?: boolean, data?: { helpful_count?: number, not_helpful_count?: number } }>}
 */
export async function postFaqFeedback(faqId, helpful) {
  if (faqId == null || faqId === "") throw new Error("FAQ id is required");
  return postJson(`faqs/${faqId}/feedback`, { helpful });
}

// Owner Notifications API functions
/**
 * Get paginated list of notifications for the authenticated owner
 * @param {Object} params - Query parameters
 * @param {number} params.per_page - Number of notifications per page (default: 15)
 * @param {string} params.type - Filter by notification type (optional)
 * @param {string} params.priority - Filter by priority: low, normal, high, urgent (optional)
 * @param {boolean} params.unread_only - Show only unread notifications (default: false)
 * @param {boolean} params.include_archived - Include archived notifications (default: false)
 * @returns {Promise}
 */
export async function getNotifications(params = {}) {
  const {
    per_page = 15,
    type = null,
    priority = null,
    unread_only = false,
    include_archived = false,
  } = params;

  const queryParams = {
    per_page,
    unread_only: unread_only.toString(),
    include_archived: include_archived.toString(),
  };

  if (type) queryParams.type = type;
  if (priority) queryParams.priority = priority;

  return getJson("owner/notifications", queryParams);
}

/**
 * Get the total count of unread notifications for the authenticated owner
 * @returns {Promise}
 */
export async function getUnreadNotificationCount() {
  return getJson("owner/notifications/unread-count");
}

/**
 * Mark a specific notification as read
 * @param {number} notificationId - The notification ID
 * @returns {Promise}
 */
export async function markNotificationAsRead(notificationId) {
  if (!notificationId) {
    throw new Error("Notification ID is required");
  }
  return postJson(`owner/notifications/${notificationId}/read`, {});
}

/**
 * Mark all unread notifications as read for the authenticated owner
 * @returns {Promise}
 */
export async function markAllNotificationsAsRead() {
  return postJson("owner/notifications/mark-all-read", {});
}

/**
 * Archive a specific notification
 * @param {number} notificationId - The notification ID
 * @returns {Promise}
 */
export async function archiveNotification(notificationId) {
  if (!notificationId) {
    throw new Error("Notification ID is required");
  }
  return postJson(`owner/notifications/${notificationId}/archive`, {});
}

/**
 * Unarchive a specific notification
 * @param {number} notificationId - The notification ID
 * @returns {Promise}
 */
export async function unarchiveNotification(notificationId) {
  if (!notificationId) {
    throw new Error("Notification ID is required");
  }
  return postJson(`owner/notifications/${notificationId}/unarchive`, {});
}

/**
 * Permanently delete a notification
 * @param {number} notificationId - The notification ID
 * @returns {Promise}
 */
export async function deleteNotification(notificationId) {
  if (!notificationId) {
    throw new Error("Notification ID is required");
  }
  return deleteJson(`owner/notifications/${notificationId}`, {});
}

// ==================== Pages API ====================

/**
 * Get shortlisted projects
 * @returns {Promise}
 */
export async function getShortlistedProjects() {
  return getJson("shortlisted-projects");
}

/**
 * Get recommended projects
 * @returns {Promise}
 */
export async function getRecommendedProjects() {
  return getJson("recommended-projects");
}

/**
 * Get page content by slug
 * @param {string} slug - Page slug (e.g., 'about-us', 'property-assistance')
 * @returns {Promise}
 */
export async function getPageBySlug(slug) {
  if (!slug) {
    throw new Error("Page slug is required");
  }
  return getJson(`pages/${slug}`);
}

/**
 * Get services pages
 * @returns {Promise}
 */
export async function getServicesPages() {
  return getJson("services-pages");
}

/**
 * Get property assistance page
 * @returns {Promise}
 */
export async function getPropertyAssistancePage() {
  return getJson("pages/property-assistance");
}

/**
 * Get property verification page
 * @returns {Promise}
 */
export async function getPropertyVerificationPage() {
  return getJson("pages/property-verification");
}

/**
 * Get other property services page
 * @returns {Promise}
 */
export async function getOtherPropertyServicesPage() {
  return getJson("pages/other-property-services");
}

/**
 * Get disclaimer / terms page content
 * GET /api/v1/pages/disclaimer
 * @returns {Promise}
 */
export async function getDisclaimerPage() {
  return getJson("pages/disclaimer");
}

/**
 * Get a single property news article by numeric id
 * GET /api/v1/property-news/{id}
 * @param {string|number} id
 * @returns {Promise<{success: boolean, data: object, navigation?: object, related_articles?: array}>}
 */
export async function getPropertyNewsArticleById(id) {
  const raw = String(id ?? "").trim();
  if (!raw || !/^\d+$/.test(raw)) {
    throw new Error("Property news article id must be numeric");
  }
  return getJson(`property-news/${raw}`);
}

/**
 * Get a single property news article by slug
 * GET /api/v1/property-news/slug/{slug}
 * @param {string} slug
 * @returns {Promise<{success: boolean, data: object, navigation?: object, related_articles?: array}>}
 */
export async function getPropertyNewsArticleBySlug(slug) {
  const raw = String(slug ?? "").trim();
  if (!raw) {
    throw new Error("Property news article slug is required");
  }
  return getJson(`property-news/slug/${encodeURIComponent(raw)}`);
}

/**
 * Get a single property news article by slug or id
 * @param {string|number} slugOrId - Article slug or numeric id
 * @returns {Promise<{success: boolean, data: object, navigation?: object, related_articles?: array}>}
 */
export async function getPropertyNewsArticle(slugOrId) {
  const raw = String(slugOrId ?? "").trim();
  if (!raw) {
    throw new Error("Property news article slug or id is required");
  }
  if (/^\d+$/.test(raw)) {
    return getPropertyNewsArticleById(raw);
  }
  return getPropertyNewsArticleBySlug(raw);
}

/**
 * Normalize GET /api/v1/cities response: `{ success, data: [{ id, name, slug, ... }] }`
 * @param {object} res - Parsed JSON from cities endpoint
 * @returns {Array<{ name: string, slug: string, image_url?: string | null }>}
 */
export function normalizeCitiesFromApiResponse(res) {
  const raw = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
  const list = [];
  for (const c of raw) {
    if (typeof c === "string") {
      const name = c.trim();
      if (name) list.push({ name, slug: name.toLowerCase().replace(/\s+/g, "-"), image_url: null });
    } else if (c && typeof c === "object") {
      const name = String(c.name || c.city || c.location || "").trim();
      const slug = String(c.slug || name.toLowerCase().replace(/\s+/g, "-") || "").trim();
      const rawImg = c.image_url ?? c.image ?? c.hero_image_url;
      const image_url =
        rawImg != null && String(rawImg).trim() !== "" ? String(rawImg).trim() : null;
      if (name && slug) list.push({ name, slug, image_url });
    }
  }
  list.sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }));
  return list;
}

/**
 * Cities list for hero dropdown. Matches `curl https://portal.proppins.com/api/v1/cities` when called with no params.
 * Optional query params only if your backend documents them (e.g. country_code).
 * @param {Object} [params] - Optional query string params
 * @returns {Promise<{ success?: boolean, data?: array }>}
 */
export async function getCities(params = {}) {
  return getJson("cities", params);
}

/**
 * Get current user's lead submissions (contacted properties).
 * @param {Object} [params] - Optional query string params if the backend supports them
 * @returns {Promise}
 */
export async function getLeadSubmissions(params = {}) {
  return getJson("lead-submissions", params, { skipCache: true });
}

/**
 * Get contacted properties for a user (by userId)
 * @param {number} userId - User ID
 * @returns {Promise}
 */
export async function getContactedProperties(userId) {
  if (!userId) {
    throw new Error("User ID is required");
  }
  return getJson(`lead-submissions/${userId}`);
}
